import React, { useEffect, useState } from "react";
import { api, fmtMoney } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, ExternalLink, QrCode, Users as UsersIcon, ClipboardList, Download } from "lucide-react";
import { QRCodeSVG, QRCodeCanvas } from "qrcode.react";
import ImagePicker from "../components/ImagePicker";

export default function OpenHouses() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(empty());
  const [qrFor, setQrFor] = useState(null);
  const [reportFor, setReportFor] = useState(null);

  const load = () => api.get("/open-houses").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/open-houses", { ...form, price: parseFloat(form.price) || 0 });
      toast.success("Open house created");
      setCreating(false); setForm(empty()); load();
    } catch { toast.error("Failed"); }
  };
  const del = async (id) => { if (window.confirm("Delete?")) { await api.delete(`/open-houses/${id}`); load(); } };
  const publicUrl = (slug) => `${window.location.origin}/oh/${slug}`;
  const copyLink = (slug) => { navigator.clipboard.writeText(publicUrl(slug)); toast.success("Link copied"); };

  return (
    <div className="space-y-7" data-testid="open-houses-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">Marketing · Lead Capture</div>
          <h1 className="font-heading text-5xl mt-2">Open House Sign-In Sheets</h1>
          <p className="text-sm text-[#050303]/70 mt-2">Auto-branded, mobile-first, offline-capable. Every sign-in becomes a contact + auto thank-you email.</p>
        </div>
        <button onClick={() => setCreating(c => !c)} className="bg-[#050303] text-white pill-btn hover:bg-[#bf858e] flex items-center gap-2" data-testid="new-oh-btn"><Plus size={16} /> New Sign-In Sheet</button>
      </header>

      {creating && (
        <form onSubmit={submit} className="core-card p-6 space-y-4" data-testid="oh-form">
          <div className="grid md:grid-cols-2 gap-3">
            <F label="Listing Title" v={form.title} onC={v => setForm({ ...form, title: v })} req testId="oh-title" />
            <F label="Address" v={form.address} onC={v => setForm({ ...form, address: v })} req testId="oh-address" />
            <F label="Price" v={form.price} onC={v => setForm({ ...form, price: v })} type="number" testId="oh-price" />
            <F label="Event Date" v={form.event_date} onC={v => setForm({ ...form, event_date: v })} placeholder="Sat Feb 28 · 2–4 PM" testId="oh-date" />
            <F label="Listing Agent Name" v={form.listing_agent_name} onC={v => setForm({ ...form, listing_agent_name: v })} testId="oh-listing-agent" />
            <F label="Hosting Agent Name" v={form.hosting_agent_name} onC={v => setForm({ ...form, hosting_agent_name: v })} testId="oh-host-agent" />
            <div className="md:col-span-2"><F label="Alert Email (new sign-in notifications)" v={form.alert_email} onC={v => setForm({ ...form, alert_email: v })} testId="oh-alert" placeholder="leave blank for account email" /></div>
          </div>
          <div>
            <Label>Brief note about the listing</Label>
            <textarea rows={3} value={form.listing_note} onChange={e => setForm({ ...form, listing_note: e.target.value })} className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm" data-testid="oh-note" />
          </div>
          <div>
            <Label>Listing photo</Label>
            <ImagePicker value={form.image_url} onChange={v => setForm({ ...form, image_url: v })} testIdPrefix="oh-img" />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="bg-[#050303] text-white pill-btn" data-testid="oh-submit">Create</button>
            <button type="button" onClick={() => setCreating(false)} className="border border-[#050303] pill-btn">Cancel</button>
          </div>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.length === 0 && !creating && <div className="col-span-full text-center text-sm text-[#050303]/55 py-10">No open houses yet.</div>}
        {items.map(oh => (
          <div key={oh.id} className="core-card p-5 flex flex-col" data-testid={`oh-${oh.id}`}>
            {oh.image_url && <img src={oh.image_url} alt="" className="w-full h-36 object-cover rounded-md mb-3" />}
            <div className="font-heading text-xl">{oh.title}</div>
            <div className="text-xs text-[#050303]/60 mt-1">{oh.address}</div>
            <div className="text-xs text-[#050303]/70 mt-2">
              {oh.event_date && <>📅 {oh.event_date}<br /></>}
              {oh.hosting_agent_name && <>Host: {oh.hosting_agent_name}<br /></>}
              {oh.listing_agent_name && <>Listed by: {oh.listing_agent_name}</>}
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs text-[#050303]/75">
              <span className="flex items-center gap-1"><UsersIcon size={12} /> {oh.signins || 0} sign-ins</span>
              {oh.price > 0 && <span className="font-semibold">{fmtMoney(oh.price)}</span>}
            </div>
            <div className="mt-4 pt-4 border-t border-[#d1cece] flex flex-wrap gap-2 items-center">
              <button onClick={() => copyLink(oh.slug)} className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303] flex items-center gap-1" data-testid={`copy-oh-${oh.id}`}><Copy size={12} /> Copy</button>
              <a href={publicUrl(oh.slug)} target="_blank" rel="noopener noreferrer" className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303] flex items-center gap-1"><ExternalLink size={12} /> Open</a>
              <button onClick={() => setQrFor(oh)} className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303] flex items-center gap-1" data-testid={`qr-oh-${oh.id}`}><QrCode size={12} /> QR</button>
              <button onClick={() => setReportFor(oh)} className="text-xs uppercase tracking-wider text-[#bf858e] hover:text-[#050303] flex items-center gap-1" data-testid={`report-oh-${oh.id}`}><ClipboardList size={12} /> Report</button>
              <button onClick={() => del(oh.id)} className="ml-auto text-[#050303]/40 hover:text-[#bf858e]"><Trash2 size={13} /></button>
            </div>
            <div className="mt-3 flex justify-center bg-[#ffffff] rounded-md p-2 border border-[#d1cece]">
              <QRCodeSVG value={publicUrl(oh.slug)} size={88} bgColor="#ffffff" fgColor="#050303" level="M" />
            </div>
          </div>
        ))}
      </div>

      {qrFor && <QrModal oh={qrFor} url={publicUrl(qrFor.slug)} onClose={() => setQrFor(null)} />}
      {reportFor && <ReportModal oh={reportFor} onClose={() => setReportFor(null)} />}
    </div>
  );
}

function empty() { return { title: "", address: "", price: "", image_url: "", listing_note: "", listing_agent_name: "", hosting_agent_name: "", event_date: "", alert_email: "" }; }
function Label({ children }) { return <label className="text-xs uppercase tracking-wider text-[#050303]/70 block mb-1">{children}</label>; }
function F({ label, v, onC, req = false, type = "text", testId, placeholder = "" }) {
  return <div><Label>{label}</Label><input required={req} type={type} value={v} onChange={e => onC(e.target.value)} placeholder={placeholder} data-testid={testId} className="w-full px-3 py-2 border border-[#d1cece] rounded-md text-sm" /></div>;
}

function QrModal({ oh, url, onClose }) {
  const download = () => {
    const c = document.getElementById(`oh-qr-${oh.id}`);
    if (!c) return;
    const l = document.createElement("a");
    l.download = `open-house-${oh.slug}.png`;
    l.href = c.toDataURL("image/png");
    l.click();
  };
  return (
    <div className="fixed inset-0 bg-[#050303]/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg p-8 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()}>
        <div className="overline mb-1">Open House QR</div>
        <h3 className="font-heading text-2xl mb-4">{oh.title}</h3>
        <div className="flex justify-center mb-4"><QRCodeCanvas id={`oh-qr-${oh.id}`} value={url} size={260} level="H" includeMargin /></div>
        <button onClick={download} className="bg-[#050303] text-white pill-btn text-sm flex items-center gap-2 mx-auto"><Download size={14} /> Download PNG</button>
      </div>
    </div>
  );
}

function ReportModal({ oh, onClose }) {
  const [rep, setRep] = useState(null);
  const [signins, setSignins] = useState([]);
  useEffect(() => {
    api.get(`/open-houses/${oh.id}/report`).then(r => setRep(r.data));
    api.get(`/open-houses/${oh.id}/signins`).then(r => setSignins(r.data || []));
  }, [oh.id]);
  return (
    <div className="fixed inset-0 bg-[#050303]/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-lg p-8 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="oh-report">
        <div className="overline mb-1">Seller Report</div>
        <h3 className="font-heading text-2xl mb-4">{oh.title}</h3>
        {!rep ? <div>Loading…</div> : (
          <>
            <div className="grid grid-cols-4 gap-3 mb-6">
              <Stat label="Sign-ins" v={rep.total} />
              <Stat label="Opt-ins" v={rep.opt_ins} />
              <Stat label="Surveys" v={rep.survey_count} />
              <Stat label="Avg Rating" v={rep.avg_rating || "—"} />
            </div>
            <div className="mb-4">
              <div className="overline mb-2">By role</div>
              {Object.entries(rep.by_role || {}).map(([k, v]) => (
                <div key={k} className="flex justify-between text-sm border-b border-[#d1cece] py-1"><span>{k}</span><span className="font-semibold">{v}</span></div>
              ))}
            </div>
            <div>
              <div className="overline mb-2">Attendees</div>
              <div className="max-h-56 overflow-y-auto">
                {signins.map(s => (
                  <div key={s.id} className="text-xs border-b border-[#d1cece] py-2">
                    <strong>{s.first_name} {s.last_name}</strong> · {s.role}<br />
                    <span className="text-[#050303]/60">{s.email} · {s.phone || "—"} · {s.permission ? "opt-in ✓" : "no opt-in"}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        <button onClick={onClose} className="mt-6 border border-[#050303] pill-btn text-sm">Close</button>
      </div>
    </div>
  );
}

function Stat({ label, v }) {
  return <div className="border border-[#d1cece] rounded-lg p-3 text-center"><div className="overline">{label}</div><div className="font-heading text-2xl mt-1">{v}</div></div>;
}
